import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { CreatorSearchCriteria } from 'server/api/services/creatorSearch/types';
import { queryToFilters } from 'lib/utils/filterUtils';
import { useUserLists } from 'lib/contexts/userListsContext';
import { CreatorTypeFilter } from 'components/Filters/CreatorTypeFilter';
import { GeoLocationFilter } from 'components/Filters/GeoLocationFilter';
import { CreatorSearchMultiFilter } from 'components/MultiFilters/CreatorSearchMultiFilter';
import { CollaboratorMultiFilter } from 'components/MultiFilters/CollaboratorMultiFilter';
import { CreatorMetadataMultiFilter } from 'components/MultiFilters/CreatorMetadataMultiFilter';
import { UserListFilter } from 'components/Filters/UserListFilter';
import { Download } from '@mui/icons-material';
import { useApplicationState } from 'lib/contexts/ApplicationState';
import { useSnackbar } from 'notistack';
import { TimeframeOptionsFilter } from 'components/Filters/TimeframeOptionsFilter';
import { defaultTimeFrameOptions } from 'components/Filters/utils';
import { CREATOR_DEFAULT_DISTANCE } from 'lib/constants';
import { FollowersFilter } from 'components/Filters/FollowersFilter';
import { userIsAdmin } from 'lib/helpers';

import { queryToCriteria } from './utils';

interface Props {
  title: string;
  /** True if there were any creators returned in the latest search */
  creatorsFound: boolean;
  onChange: (criteria: CreatorSearchCriteria) => void;
}

export const FilterCriteria = ({ title, onChange, creatorsFound }: Props) => {
  const router = useRouter();
  const { lists } = useUserLists();
  const { currentUser, api } = useApplicationState();
  const { enqueueSnackbar } = useSnackbar();
  const [canExportCSV, setCanExportCSV] = useState(false);
  const [criteria, setCriteria] = useState<CreatorSearchCriteria | undefined>();

  const exportCreators = async () => {
    try {
      await api.exportCreatorsCSVReport(criteria as CreatorSearchCriteria, title);
      enqueueSnackbar('Your creator export is running now. You will receive an email with a link to download when it is complete.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(`The export failed to start because of an error ${err}`, { variant: 'error' });
    }
  };

  useEffect(() => {
    // Only show the CSV export if the creator search involves a list with a jobId.
    // (Only team members can see lists with a jobId.)
    if (criteria?.list && lists?.find((l) => l.publicId === criteria?.list)?.jobId) {
      setCanExportCSV(true);
    } else {
      setCanExportCSV(false);
    }
  }, [lists, currentUser, criteria]);

  useEffect(() => {
    if (criteria) onChange(criteria);
  }, [criteria]);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    setCriteria(queryToCriteria(router.query));
  }, [router]);

  if (!criteria) return null;
  const multiFilterLabel = `Search @${criteria.userType === 'company' ? 'companies' : 'creators'}, roles, #tags, #events, keywords`;

  return (
    <div className="max-w-screen-2xl m-auto">
      <div className="flex flex-wrap md:flex-nowrap gap-2 justify-between">
        <div className="w-full md:w-2/5 flex items-center pb-2">
          <UserListFilter types={['list', 'team']} label="List" />
          <div className="pl-2">
            <CreatorTypeFilter
              defaultType="creator"
              selected={criteria.userType}
            />
          </div>
        </div>
        <div className="w-full md:w-3/5 pb-2">
          <CreatorSearchMultiFilter
            filters={queryToFilters(router.query)}
            creatorType={criteria.userType}
            label={multiFilterLabel}
          />
        </div>
      </div>
      <div className="flex justify-between gap-2">
        <div className="flex flex-wrap md:flex-nowrap w-full gap-2">
          <div className="w-full md:w-1/4 xl:w-1/5">
            <GeoLocationFilter defaultDistance={CREATOR_DEFAULT_DISTANCE} />
          </div>
          <div className="w-full md:w-1/4 xl:w-2/5">
            <CollaboratorMultiFilter
              filters={queryToFilters(router.query, ['byCollaboratorType', 'byCollaboratorRole', 'byCollaborator'])}
            />
          </div>
          <div className="w-full md:w-1/4 xl:w-1/5">
            <TimeframeOptionsFilter
              minPostedAt={criteria?.minPostedAt}
              maxPostedAt={criteria?.maxPostedAt}
              options={defaultTimeFrameOptions}
            />
          </div>
          <div className="w-full md:w-1/4 xl:w-1/5">
            <FollowersFilter
              followersRange={{
                ...criteria.minFollowers && { minFollowers: criteria.minFollowers },
                ...criteria.maxFollowers && { maxFollowers: criteria.maxFollowers },
              }}
            />
          </div>
          {userIsAdmin(currentUser) && (
            <div className="w-full md:w-1/4 xl:w-1/5">
              <CreatorMetadataMultiFilter
                filters={queryToFilters(router.query, ['brands', 'people', 'companies', 'industries', 'products'])}
              />
            </div>
          )}
          {canExportCSV && creatorsFound && (
            <div className="w-full md:w-1/4 xl:w-1/5 flex justify-between items-center">
              <div className="flex gap-2">
                <button type="button" className="ml-5" aria-label="download csv" onClick={exportCreators}>
                  CSV
                  {' '}
                  <Download />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
